// Service d'authentification pour CLIENTS
// Utilise l'API backend avec JWT tokens
import apiService from './apiService';
import { LEGAL_VERSION } from './LegalGate';

const authService = {
  // Inscription
  register: async (userData) => {
    try {
      const response = await apiService.post('/auth/register/user', {
        name: userData.name,
        phone: userData.phone,
        email: userData.email || null,
        dateOfBirth: userData.dateOfBirth || null,
        password: userData.password,
        photo: userData.photo,
        terms_version: LEGAL_VERSION,
        phone_verification_token: userData.phone_verification_token, // V7 : code WhatsApp confirmé avant la création
        ...(userData.bot || {}), // piège, durée de remplissage, jeton captcha (BotShield)
      }, { userType: 'user' });  // Utilise le timeout par défaut de 30s

      if (response.success) {
        apiService.setToken(response.token, 'user');
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        return { success: true, user: response.user, phone_verified: response.phone_verified };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de l\'inscription'
      };
    }
  },

  // Connexion
  login: async (phone, password) => {
    try {
      const response = await apiService.post('/auth/login/user', {
        phone,
        password
      }, { userType: 'user' });

      if (response.success) {
        apiService.setToken(response.token, 'user');
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        // V7 : phone_verified false → compte créé sans code (ancienne app) → la page demande le code
        return { success: true, user: response.user, phone_verified: response.phone_verified };
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de la connexion'
      };
    }
  },

  // ── Connexion par téléphone (2026-10-30) ──────────────────────────────────────
  // Réponse de /auth/otp-login → session (jeton 7 j + jeton d'appareil 180 j)
  persistOtpSession: async (resp) => {
    if (!resp?.success || !resp.token) return resp;
    apiService.setToken(resp.token, 'user');
    localStorage.setItem('currentUser', JSON.stringify(resp.user));
    if (resp.refresh_token) apiService.setRefreshToken(resp.refresh_token);
    return { success: true, user: resp.user, is_new: !!resp.is_new };
  },
  hasRefresh: () => !!apiService.getRefreshToken(),
  // Au chargement : jeton d'appareil → nouveau jeton d'accès, sans code WhatsApp
  restoreSession: async () => {
    const ok = await apiService.silentRefresh('user');
    const user = ok ? authService.getCurrentUser() : null;
    return ok && user ? { success: true, user } : { success: false };
  },

  // Déconnexion (coupe aussi le jeton d'appareil de ce navigateur, sans bloquer)
  logout: () => {
    const rt = apiService.getRefreshToken();
    if (rt) apiService.post('/auth/logout-device', { refresh_token: rt }).catch(() => {});
    apiService.removeRefreshToken();
    localStorage.removeItem('currentUser');
    apiService.removeToken('user');
    return { success: true };
  },

  // Vérifier si connecté
  isAuthenticated: () => {
    return !!apiService.getToken('user') && !!localStorage.getItem('currentUser');
  },

  // Récupérer l'utilisateur actuel
  getCurrentUser: () => {
    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  },

  // Mot de passe oublié - Étape 1 : Demander un code de réinitialisation
  // V7 : options { channel: 'auto' | 'email', language } — WhatsApp d'abord, e-mail en secours
  forgotPassword: async (phone, options = {}) => {
    try {
      const response = await apiService.post('/auth/forgot-password/user', { phone, ...options });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de l\'envoi du code'
      };
    }
  },

  // Mot de passe oublié - Étape 2 : Vérifier le code
  verifyResetCode: async (phone, code) => {
    try {
      const response = await apiService.post('/auth/verify-reset-code/user', { phone, code });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de la vérification du code'
      };
    }
  },

  // Mot de passe oublié - Étape 3 : Réinitialiser le mot de passe
  resetPassword: async (phone, code, newPassword) => {
    try {
      const response = await apiService.post('/auth/reset-password/user', { phone, code, newPassword });
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.error || 'Erreur lors de la réinitialisation du mot de passe'
      };
    }
  }
};

export default authService;